import { Link } from 'react-router-dom';

/**
 * The sales page, built to the formed-in-the-quiet bar: a photographic
 * full-bleed hero with a color wash, fluid clamp() display type with italic
 * serif accents, full-width bands that give the page chapters, a dark quote
 * band as the emotional beat, and the product's own artifacts as proof (a
 * real card, a mock iMessage unfurl of a real link).
 *
 * Every image is the founder's own trip, served from our R2.
 */

const EXAMPLE_TRIP_URL =
  'https://heresmytrip.app/t/british-virgin-islands-in-july-2024-vtju6q?k=tnp-7nta63Tgr_BwsAadVLyy.CaP0bIX945618qyMQ3neMwXEVOv';

const HERO_IMG = '/media/og/e0f221e14617dc31839509c53cb15d4be8fe05e5e6711953e06406fa65cf106e';
const CARD_IMG = '/media/og/0f29f3dee6ed83d3b07b384b81b89b01dac8496c02d806c7b9930291baa269c0';

export function Landing() {
  return (
    <main>
      {/* HERO — the question, asked over the water it came from. */}
      <section
        className="relative overflow-hidden text-surface"
        style={{
          backgroundImage: `linear-gradient(150deg, rgba(21,32,28,0.88) 0%, rgba(31,79,64,0.72) 55%, rgba(35,32,29,0.55) 100%), url(${HERO_IMG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center 40%',
        }}
      >
        <div className="mx-auto max-w-4xl px-6 pb-24 pt-28 sm:pb-32 sm:pt-36">
          <h1 className="font-display text-[clamp(2.75rem,7vw,5.25rem)] font-normal leading-[1.02] tracking-[-0.02em]">
            “What did you guys do that was{' '}
            <em className="font-light italic text-[#E9DCC3]">so fun</em>?”
          </h1>
          <p className="mt-7 max-w-xl font-body text-[clamp(1.0625rem,2vw,1.3125rem)] font-light leading-relaxed text-surface/90">
            Every good trip ends with that text. Here&rsquo;s My Trip turns your
            answer into one page: the exact places you stayed, ate, and loved,
            each with your note on it, ready to send to anyone who asks.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link
              to="/trips/new"
              className="rounded-btn bg-surface px-6 py-3 font-util text-[0.9375rem] font-semibold text-ink transition-colors hover:bg-raised"
            >
              Start your trip page
            </Link>
            <a
              href={EXAMPLE_TRIP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-btn px-5 py-3 font-util text-[0.9375rem] font-medium text-surface/85 transition-colors hover:text-surface"
            >
              See a real one
            </a>
          </div>

          <div className="mt-14 flex flex-wrap gap-x-12 gap-y-6 border-t border-surface/20 pt-7">
            <div>
              <strong className="block font-display text-[1.75rem] font-normal">
                3 minutes
              </strong>
              <span className="font-util text-meta text-surface/80">of talking, start to send</span>
            </div>
            <div>
              <strong className="block font-display text-[1.75rem] font-normal">1 link</strong>
              <span className="font-util text-meta text-surface/80">no app for your friends</span>
            </div>
            <div>
              <strong className="block font-display text-[1.75rem] font-normal">$0</strong>
              <span className="font-util text-meta text-surface/80">free to make and send</span>
            </div>
          </div>
        </div>
      </section>

      {/* THE ORIGIN — dark band, the founder's line as the emotional beat. */}
      <section className="bg-ink px-6 py-14 text-center">
        <p className="mx-auto max-w-2xl font-display text-[clamp(1.25rem,2.4vw,1.625rem)] font-light italic leading-snug text-surface/90">
          I made this after two weeks in the British Virgin Islands, when the
          fourth person asked what we did that was so fun. Now the answer is one
          link.
        </p>
        <cite className="mt-5 block font-util text-[0.75rem] font-medium uppercase not-italic tracking-[0.22em] text-[#E9DCC3]">
          Jeff · who built Here&rsquo;s My Trip
        </cite>
      </section>

      {/* HOW — the anti-work argument, with room to breathe. */}
      <section className="mx-auto max-w-4xl px-6 py-24 sm:py-28">
        <h2 className="max-w-2xl font-display text-[clamp(2rem,4.5vw,3.25rem)] font-normal leading-[1.05] tracking-[-0.02em] text-ink">
          Three minutes of{' '}
          <em className="font-light italic text-accent">remembering out loud</em>
        </h2>
        <ol className="mt-12 max-w-2xl space-y-10">
          <li className="flex gap-6">
            <span className="font-display text-[1.75rem] font-normal leading-none text-accent">
              1
            </span>
            <p className="font-body text-[1.0625rem] leading-relaxed text-ink/85">
              Say where you went. Dates are two taps, and &ldquo;July&rdquo; is a
              perfectly good answer.
            </p>
          </li>
          <li className="flex gap-6">
            <span className="font-display text-[1.75rem] font-normal leading-none text-accent">
              2
            </span>
            <p className="font-body text-[1.0625rem] leading-relaxed text-ink/85">
              Talk about it the way you&rsquo;d text a friend. Name the boat, the
              villa, the pizza place. While you type, it finds each one&rsquo;s
              actual website, photo, and map pin for you.
            </p>
          </li>
          <li className="flex gap-6">
            <span className="font-display text-[1.75rem] font-normal leading-none text-accent">
              3
            </span>
            <p className="font-body text-[1.0625rem] leading-relaxed text-ink/85">
              Send one link. Nothing to install on their end, no account to make,
              the whole page just opens.
            </p>
          </li>
        </ol>
        <p className="mt-10 font-util text-meta text-muted">
          Already typed it all out for someone once? Paste that text and the page
          builds itself from it.
        </p>
      </section>

      {/* PROOF — cream band, a real card with real weight. */}
      <section className="bg-raised px-6 py-24 sm:py-28">
        <div className="mx-auto max-w-4xl">
          <h2 className="max-w-2xl font-display text-[clamp(2rem,4.5vw,3.25rem)] font-normal leading-[1.05] tracking-[-0.02em] text-ink">
            Specific enough <em className="font-light italic text-accent">to book</em>
          </h2>
          <p className="mt-6 max-w-xl font-body text-[1.0625rem] leading-relaxed text-ink/85">
            &ldquo;A great house on Tortola&rdquo; helps nobody plan anything. A
            trip page carries the listing itself. This is a real card from a real
            page:
          </p>

          <article className="mt-12 max-w-xl rounded-note bg-white p-5 shadow-[0_24px_60px_rgba(35,32,29,0.18)]">
            <img
              src={CARD_IMG}
              alt=""
              loading="lazy"
              className="aspect-[16/9] w-full rounded-img object-cover bg-raised"
            />
            <h3 className="mt-4 font-util text-place font-semibold text-ink">
              <a
                href="https://www.vrbo.com/5402751"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-colors hover:text-accent"
              >
                Tortola House
              </a>
            </h3>
            <p className="mt-1 font-util text-meta text-muted">vrbo.com/5402751</p>
            <p className="note-aside mt-3">
              We also stayed a second week at a house on Tortola. This was a
              totally different type of adventure. We rented an SUV and drove on
              the WILD roads around Tortola to get to some awesome beaches. The
              house was great, and had a housekeeper named Ms. Ivet who was
              wonderful.
            </p>
          </article>
          <p className="mt-4 font-util text-meta text-muted">
            The photo and link come from the listing. The words are the
            traveler&rsquo;s own, kept exactly as written.
          </p>
        </div>
      </section>

      {/* RECEIVING — show the text-message card instead of describing it. */}
      <section className="mx-auto max-w-4xl px-6 py-24 sm:py-28">
        <div className="grid items-center gap-12 sm:grid-cols-2">
          <div>
            <h2 className="font-display text-[clamp(2rem,4.5vw,3.25rem)] font-normal leading-[1.05] tracking-[-0.02em] text-ink">
              One link that opens{' '}
              <em className="font-light italic text-accent">for everyone</em>
            </h2>
            <p className="mt-6 font-body text-[1.0625rem] leading-relaxed text-ink/85">
              Text it and it shows up like this. Your friend taps once and sees
              the whole page. One more tap puts it on their wishlist, and they
              can copy your whole itinerary as a starting point for their own
              trip.
            </p>
          </div>
          <div className="justify-self-center sm:justify-self-end">
            {/* Mock iMessage unfurl of the real example link. */}
            <div className="w-[300px] overflow-hidden rounded-2xl bg-white shadow-[0_18px_44px_rgba(35,32,29,0.16)]">
              <img src={HERO_IMG} alt="" loading="lazy" className="aspect-[1.91/1] w-full object-cover" />
              <div className="px-4 py-3">
                <p className="font-util text-[0.9375rem] font-semibold leading-snug text-ink">
                  How to spend two weeks in the British Virgin Islands
                </p>
                <p className="mt-1 font-util text-[0.75rem] text-muted">heresmytrip.app</p>
              </div>
            </div>
            <p className="mt-3 text-center font-util text-meta text-muted sm:text-right">
              a real link, as it lands in a text
            </p>
          </div>
        </div>
      </section>

      {/* CLOSE — dark band, the promise, one button. */}
      <section className="bg-ink px-6 py-24 text-center sm:py-28">
        <h2 className="mx-auto max-w-2xl font-display text-[clamp(2.25rem,5vw,3.75rem)] font-normal leading-[1.05] tracking-[-0.02em] text-surface">
          The next time someone asks,{' '}
          <em className="font-light italic text-[#E9DCC3]">you&rsquo;ll have the answer</em>
        </h2>
        <div className="mt-10">
          <Link
            to="/trips/new"
            className="inline-block rounded-btn bg-surface px-7 py-3.5 font-util text-[0.9375rem] font-semibold text-ink transition-colors hover:bg-raised"
          >
            Start your trip page
          </Link>
        </div>
        <p className="mt-14 font-util text-[0.75rem] text-surface/50">
          Here&rsquo;s My Trip
        </p>
      </section>
    </main>
  );
}
